"use client"

import "react-datepicker/dist/react-datepicker.css"
import "@/app/styles/components/travel-date-range.scss"
import React, { useEffect, useRef, useState, type ComponentType } from "react"
import dynamic from "next/dynamic"
import type { DatePickerProps } from "react-datepicker"
import AppIcon from "@/app/components/atomic/AppIcon"
import { useClickOutside } from "@/app/hooks/useClickOutside"
import TripFieldCard from "./TripFieldCard"
import { formatIsoShortDate } from "../travelFormat"

const loadDatePicker = async () => {
    const [reactDatePicker, { fr }] = await Promise.all([
        import("react-datepicker"),
        import("date-fns/locale/fr"),
    ])
    reactDatePicker.registerLocale("fr", fr)
    return reactDatePicker.default as unknown as ComponentType<DatePickerProps>
}

// Calendrier deux mois (départ → retour), locale FR, rendu inline dans un panneau qu'on positionne
// nous-mêmes sous le champ (pas le popper de react-datepicker, qui recouvrait la barre de recherche).
// `loading` = squelette dimensionné : sans lui, le panneau se réduit à une pastille grise (bordure
// arrondie sur boîte vide) le temps du chargement du chunk — visible au 1er clic sur cache froid.
const DatePicker = dynamic<DatePickerProps>(loadDatePicker, {
    ssr: false,
    loading: () => <div className="h-[280px] w-[520px] max-w-full animate-pulse rounded-lg bg-gray-50" />,
})

const toDate = (iso: string): Date | undefined => (iso ? new Date(`${iso}T00:00:00`) : undefined)

const toIso = (date: Date | null): string => {
    if (!date) {
        return ""
    }
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

const shortDate = (iso: string): string | null => (iso ? formatIsoShortDate(iso) : null)

interface TravelDateRangeFieldProps {
    startDate: string
    endDate: string
    minDate: string
    onChange: (startDate: string, endDate: string) => void
}

export default function TravelDateRangeField({ startDate, endDate, minDate, onChange }: TravelDateRangeFieldProps) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useClickOutside(ref, () => setOpen(false))

    // Précharge le chunk react-datepicker dès le montage → 1re ouverture instantanée.
    useEffect(() => {
        void loadDatePicker()
    }, [])

    const start = shortDate(startDate)
    const end = shortDate(endDate)

    return (
        <div ref={ref} className="relative min-w-0">
            <TripFieldCard
                label="Dates de voyage"
                mobileLabel="Dates du voyage"
                icon={<AppIcon iconClassName="fa-calendar" type="regular" size="sm" className="text-[#4a5565]" />}
                value={
                    start ? (
                        <span className="flex items-center gap-1.5">
                            {start}
                            <AppIcon iconClassName="fa-arrow-right" size="xxs" className="text-[#4a5565]" />
                            {end ?? "…"}
                        </span>
                    ) : null
                }
                placeholder="Aller → Retour"
                open={open}
                onToggle={() => setOpen((current) => !current)}
                panelClassName="max-w-[92vw] p-2 sm:max-h-[70vh] sm:overflow-auto"
            >
                <DatePicker
                    inline
                    selectsRange
                    monthsShown={2}
                    locale="fr"
                    minDate={toDate(minDate)}
                    startDate={toDate(startDate)}
                    endDate={toDate(endDate)}
                    calendarClassName="travel-range-calendar"
                    onChange={((range: [Date | null, Date | null]) => {
                        onChange(toIso(range[0]), toIso(range[1]))
                        if (range[0] && range[1]) {
                            setOpen(false)
                        }
                    }) as never}
                />
            </TripFieldCard>
        </div>
    )
}
